/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { FloatingPixelBackground, PixelLogo } from '../components/pixel/PixelComponents';

const PrivacyPolicy: React.FC = () => {
  const lastUpdated = 'January 31, 2026';
  const effectiveDate = 'January 31, 2026';

  return (
    <div className="min-h-screen bg-black text-gray-100 relative">
      {/* FloatingPixelBackground self-clips via `fixed inset-0 overflow-hidden`; an
          overflow-hidden here would instead become the sticky header's clipping
          ancestor and break `position: sticky` in Safari. */}
      <FloatingPixelBackground intensity="low" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-primary-500/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PixelLogo scale={0.8} animate={false} />
              <span className="text-xl font-bold text-primary-500">Podstack</span>
            </div>
            <Link
              to="/login"
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-800 hover:border-primary-500/50 rounded-lg transition-all"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16 relative z-10">
        {/* Title */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Privacy <span className="text-primary-500">Policy</span>
          </h1>
          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
            <span>Last Updated: {lastUpdated}</span>
            <span>•</span>
            <span>Effective Date: {effectiveDate}</span>
          </div>
        </div>

        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-4">Table of Contents</h2>
          <ol className="grid md:grid-cols-2 gap-2 text-sm text-gray-400">
            {[
              'Introduction',
              'Data Controller',
              'Information We Collect',
              'How We Use Your Information',
              'Legal Basis for Processing',
              'Data Retention',
              'Your Rights Under GDPR',
              'Your Rights Under DPDP (India)',
              'International Data Transfers',
              'Cookies and Tracking Technologies',
              'Third-Party Services',
              'Data Security',
              "Children's Privacy",
              'Changes to This Policy',
              'Contact Us',
            ].map((item, index) => (
              <li key={index}>
                <a href={`#section-${index + 1}`} className="hover:text-primary-400 transition-colors">
                  {index + 1}. {item}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Content Sections */}
        <div className="space-y-12">

          {/* Section 1: Introduction */}
          <section id="section-1">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              1. Introduction
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              Welcome to Podstack ("we," "our," or "us"). We are committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>podstack.ai</strong> and use our GPU cloud computing services (collectively, the "Services").
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              This Privacy Policy is designed to comply with:
            </p>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li><strong>General Data Protection Regulation (GDPR)</strong> – European Union</li>
              <li><strong>Digital Personal Data Protection Act, 2023 (DPDP)</strong> – India</li>
              <li><strong>ePrivacy Directive</strong> – European Union</li>
              <li><strong>Information Technology Act, 2000</strong> – India</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our Services.
            </p>
          </section>

          {/* Section 2: Data Controller */}
          <section id="section-2">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              2. Data Controller
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              For the purposes of the GDPR and DPDP, the data controller responsible for your personal data is:
            </p>
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800 mt-4">
              <p className="text-white font-semibold mb-2">Razordynamics Private Limited (operating as Podstack)</p>
              <p className="text-gray-400 text-sm">
                Address: 11th Floor, Prestige Tech Park, Platina 2, Outer Ring Rd, Kadubeesanahalli, Bengaluru, Karnataka India- 560087<br />
                Phone: <a href="tel:+919620598447" className="text-primary-400 hover:underline">+91 96205 98447</a><br />
                Email: <a href="mailto:privacy@podstack.ai" className="text-primary-400 hover:underline">privacy@podstack.ai</a><br />
                Data Protection Officer: <a href="mailto:dpo@podstack.ai" className="text-primary-400 hover:underline">dpo@podstack.ai</a>
              </p>
            </div>
            <p className="text-gray-300 leading-relaxed mt-4">
              For users in the European Economic Area (EEA), we act as the data controller for personal data we collect through our Services. For users in India, we act as the Data Fiduciary under the DPDP Act.
            </p>
          </section>

          {/* Section 3: Information We Collect */}
          <section id="section-3">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              3. Information We Collect
            </h2>

            <h3 className="text-xl font-semibold text-white mt-6">3.1 Information You Provide Directly</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li><strong>Account Information:</strong> Name, email address, phone number, password, and organization name when you register for an account.</li>
              <li><strong>Billing Information:</strong> Payment card details, billing address, GST/VAT identification numbers, and transaction history.</li>
              <li><strong>Communications:</strong> Information you provide when contacting our support team, including chat logs and email correspondence.</li>
              <li><strong>User Content:</strong> Data, files, code, models, and other content you upload, store, or process using our Services.</li>
              <li><strong>Feedback:</strong> Surveys, reviews, and feedback you voluntarily submit.</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6">3.2 Information Collected Automatically</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers, and hardware configuration.</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, click patterns, session duration, and interaction with our Services.</li>
              <li><strong>Log Data:</strong> Server logs, error reports, and system activity logs.</li>
              <li><strong>Location Data:</strong> Approximate geographic location based on IP address.</li>
              <li><strong>Cookies and Similar Technologies:</strong> Information collected through cookies, web beacons, and similar tracking technologies (see Section 10).</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6">3.3 Information from Third Parties</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li><strong>Authentication Providers:</strong> If you sign in using Google, GitHub, or other OAuth providers, we receive your basic profile information.</li>
              <li><strong>Payment Processors:</strong> Transaction confirmations and fraud prevention data from payment service providers.</li>
              <li><strong>Business Partners:</strong> Information from referral partners and resellers.</li>
            </ul>
          </section>

          {/* Section 4: How We Use Your Information */}
          <section id="section-4">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              4. How We Use Your Information
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              We use the information we collect for the following purposes:
            </p>

            <h3 className="text-xl font-semibold text-white mt-6">4.1 Service Delivery</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li>Provide, maintain, and improve our GPU cloud computing services</li>
              <li>Process your transactions and manage your account</li>
              <li>Allocate computing resources and manage infrastructure</li>
              <li>Provide technical support and respond to inquiries</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6">4.2 Communication</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li>Send service-related notifications (usage alerts, billing updates, security notices)</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Send marketing communications (with your consent)</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6">4.3 Analytics and Improvement</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li>Analyze usage patterns to improve our Services</li>
              <li>Develop new features and functionality</li>
              <li>Conduct research and analysis</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6">4.4 Security and Compliance</h3>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li>Detect, prevent, and address technical issues, fraud, and security threats</li>
              <li>Enforce our Terms of Service and acceptable use policies</li>
              <li>Comply with legal obligations and respond to lawful requests</li>
            </ul>
          </section>

          {/* Section 5: Legal Basis for Processing (GDPR) */}
          <section id="section-5">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              5. Legal Basis for Processing (GDPR)
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              Under the GDPR, we process your personal data based on the following legal grounds:
            </p>

            <div className="overflow-x-auto mt-6">
              <table className="w-full text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-white">Legal Basis</th>
                    <th className="text-left py-3 px-4 font-semibold text-white">Processing Activities</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  <tr>
                    <td className="py-3 px-4 font-medium text-primary-400">Contract Performance<br /><span className="text-xs text-gray-500">Art. 6(1)(b) GDPR</span></td>
                    <td className="py-3 px-4">Account creation, service delivery, billing, technical support</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-primary-400">Consent<br /><span className="text-xs text-gray-500">Art. 6(1)(a) GDPR</span></td>
                    <td className="py-3 px-4">Marketing communications, non-essential cookies, analytics</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-primary-400">Legitimate Interests<br /><span className="text-xs text-gray-500">Art. 6(1)(f) GDPR</span></td>
                    <td className="py-3 px-4">Fraud prevention, security monitoring, service improvement, business analytics</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-primary-400">Legal Obligation<br /><span className="text-xs text-gray-500">Art. 6(1)(c) GDPR</span></td>
                    <td className="py-3 px-4">Tax compliance, responding to legal requests, data retention requirements</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 6: Data Retention */}
          <section id="section-6">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              6. Data Retention
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>

            <div className="overflow-x-auto mt-6">
              <table className="w-full text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-white">Data Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-white">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  <tr>
                    <td className="py-3 px-4">Account Information</td>
                    <td className="py-3 px-4">Duration of account + 3 years after deletion</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Billing & Transaction Records</td>
                    <td className="py-3 px-4">7 years (tax/legal compliance)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">User Content & Data</td>
                    <td className="py-3 px-4">Until you delete it or account termination + 30 days</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Usage Logs</td>
                    <td className="py-3 px-4">90 days (rolling)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Support Communications</td>
                    <td className="py-3 px-4">3 years from resolution</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Marketing Consent Records</td>
                    <td className="py-3 px-4">Until withdrawal + 3 years (proof of consent)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Cookie Consent Records</td>
                    <td className="py-3 px-4">7 years (GDPR compliance audit trail)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 7: Your Rights Under GDPR */}
          <section id="section-7">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              7. Your Rights Under GDPR
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have the following rights under the GDPR:
            </p>

            <div className="grid gap-4 mt-6">
              {[
                { title: 'Right of Access (Art. 15)', desc: 'Request a copy of the personal data we hold about you.' },
                { title: 'Right to Rectification (Art. 16)', desc: 'Request correction of inaccurate or incomplete personal data.' },
                { title: 'Right to Erasure (Art. 17)', desc: 'Request deletion of your personal data ("Right to be Forgotten").' },
                { title: 'Right to Restriction (Art. 18)', desc: 'Request restriction of processing of your personal data.' },
                { title: 'Right to Data Portability (Art. 20)', desc: 'Receive your data in a structured, machine-readable format.' },
                { title: 'Right to Object (Art. 21)', desc: 'Object to processing based on legitimate interests or for direct marketing.' },
                { title: 'Rights Related to Automated Decision-Making (Art. 22)', desc: 'Not be subject to decisions based solely on automated processing.' },
                { title: 'Right to Withdraw Consent (Art. 7)', desc: 'Withdraw consent at any time where processing is based on consent.' },
              ].map((right, index) => (
                <div key={index} className="p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                  <h4 className="font-semibold text-primary-400">{right.title}</h4>
                  <p className="text-gray-400 text-sm mt-1">{right.desc}</p>
                </div>
              ))}
            </div>

            <p className="text-gray-300 leading-relaxed mt-6">
              To exercise these rights, please contact us at <a href="mailto:privacy@podstack.ai" className="text-primary-400 hover:underline">privacy@podstack.ai</a>. We will respond to your request within 30 days. You also have the right to lodge a complaint with your local Data Protection Authority.
            </p>
          </section>

          {/* Section 8: Your Rights Under DPDP (India) */}
          <section id="section-8">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              8. Your Rights Under DPDP (India)
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              If you are located in India, you have the following rights under the Digital Personal Data Protection Act, 2023:
            </p>

            <div className="grid gap-4 mt-6">
              {[
                { title: 'Right to Access Information', desc: 'Obtain a summary of your personal data and processing activities.' },
                { title: 'Right to Correction and Erasure', desc: 'Request correction of inaccurate data and erasure of data no longer necessary.' },
                { title: 'Right to Grievance Redressal', desc: 'Have your grievances addressed by our designated Grievance Officer.' },
                { title: 'Right to Nominate', desc: 'Nominate another person to exercise your rights in case of death or incapacity.' },
              ].map((right, index) => (
                <div key={index} className="p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                  <h4 className="font-semibold text-primary-400">{right.title}</h4>
                  <p className="text-gray-400 text-sm mt-1">{right.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-gray-900/50 rounded-xl p-6 border border-primary-500/30 mt-6">
              <h4 className="font-semibold text-white mb-2">Grievance Officer (India)</h4>
              <p className="text-gray-400 text-sm">
                Name: Grievance Officer, Podstack<br />
                Email: <a href="mailto:grievance@podstack.ai" className="text-primary-400 hover:underline">grievance@podstack.ai</a><br />
                Response Time: Within 30 days of receipt of complaint
              </p>
            </div>

            <p className="text-gray-300 leading-relaxed mt-6">
              You may also file a complaint with the Data Protection Board of India if you believe your rights have been violated.
            </p>
          </section>

          {/* Section 9: International Data Transfers */}
          <section id="section-9">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              9. International Data Transfers
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              Your personal data may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6">9.1 Transfers from the EEA/UK</h3>
            <p className="text-gray-300 leading-relaxed mt-2">
              When we transfer personal data from the EEA or UK to countries not deemed adequate by the European Commission, we use:
            </p>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li><strong>Standard Contractual Clauses (SCCs)</strong> approved by the European Commission</li>
              <li><strong>Binding Corporate Rules</strong> where applicable</li>
              <li><strong>Adequacy decisions</strong> for countries recognized as providing adequate protection</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mt-6">9.2 Transfers from India</h3>
            <p className="text-gray-300 leading-relaxed mt-2">
              Cross-border transfers of personal data from India are conducted in compliance with the DPDP Act and any rules notified by the Central Government regarding permitted jurisdictions.
            </p>
          </section>

          {/* Section 10: Cookies and Tracking Technologies */}
          <section id="section-10">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              10. Cookies and Tracking Technologies
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              We use cookies and similar tracking technologies to collect and track information about your activity on our Services.
            </p>

            <h3 className="text-xl font-semibold text-white mt-6">10.1 Types of Cookies We Use</h3>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-white">Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-white">Purpose</th>
                    <th className="text-left py-3 px-4 font-semibold text-white">Consent Required</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  <tr>
                    <td className="py-3 px-4 font-medium text-green-400">Essential</td>
                    <td className="py-3 px-4">Authentication, security, basic functionality</td>
                    <td className="py-3 px-4">No (strictly necessary)</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-blue-400">Analytics</td>
                    <td className="py-3 px-4">Usage statistics, performance monitoring</td>
                    <td className="py-3 px-4">Yes</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-purple-400">Marketing</td>
                    <td className="py-3 px-4">Advertising, retargeting, conversion tracking</td>
                    <td className="py-3 px-4">Yes</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-yellow-400">Personalization</td>
                    <td className="py-3 px-4">User preferences, customized experience</td>
                    <td className="py-3 px-4">Yes</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-white mt-6">10.2 Managing Cookies</h3>
            <p className="text-gray-300 leading-relaxed mt-2">
              You can manage your cookie preferences through:
            </p>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li>Our cookie consent banner (displayed on first visit)</li>
              <li>The "Cookie Settings" link in our website footer</li>
              <li>Your browser settings (note: disabling cookies may affect functionality)</li>
            </ul>
          </section>

          {/* Section 11: Third-Party Services */}
          <section id="section-11">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              11. Third-Party Services
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              We use the following categories of third-party service providers:
            </p>

            <div className="overflow-x-auto mt-6">
              <table className="w-full text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-white">Category</th>
                    <th className="text-left py-3 px-4 font-semibold text-white">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  <tr>
                    <td className="py-3 px-4">Cloud Infrastructure Providers</td>
                    <td className="py-3 px-4">Hosting and compute resources</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Payment Processors (Razorpay)</td>
                    <td className="py-3 px-4">Secure payment processing</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Analytics Services (Google Analytics)</td>
                    <td className="py-3 px-4">Website analytics and performance</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Communication Tools</td>
                    <td className="py-3 px-4">Email delivery, chat support</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Authentication Providers</td>
                    <td className="py-3 px-4">OAuth login (Google, GitHub)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-300 leading-relaxed mt-4">
              These third parties process data on our behalf and are bound by data processing agreements that ensure appropriate protection of your personal data.
            </p>
          </section>

          {/* Section 12: Data Security */}
          <section id="section-12">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              12. Data Security
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              We implement appropriate technical and organizational measures to protect your personal data, including:
            </p>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li><strong>Encryption:</strong> TLS 1.3 for data in transit; AES-256 for data at rest</li>
              <li><strong>Access Controls:</strong> Role-based access, multi-factor authentication</li>
              <li><strong>Infrastructure Security:</strong> Firewalls, intrusion detection, regular security audits</li>
              <li><strong>Data Isolation:</strong> Logical separation of customer data</li>
              <li><strong>Monitoring:</strong> 24/7 security monitoring and incident response</li>
              <li><strong>Employee Training:</strong> Regular security and privacy awareness training</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              While we strive to protect your personal data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 13: Children's Privacy */}
          <section id="section-13">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              13. Children's Privacy
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              Our Services are not directed to individuals under the age of 18. We do not knowingly collect personal data from children. If you are a parent or guardian and believe your child has provided us with personal data, please contact us at <a href="mailto:privacy@podstack.ai" className="text-primary-400 hover:underline">privacy@podstack.ai</a>, and we will take steps to delete such information.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              Under the DPDP Act (India), processing of children's data requires verifiable parental consent. We do not process children's data.
            </p>
          </section>

          {/* Section 14: Changes to This Policy */}
          <section id="section-14">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              14. Changes to This Policy
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make material changes:
            </p>
            <ul className="text-gray-300 space-y-2 mt-2 list-disc list-inside">
              <li>We will update the "Last Updated" date at the top of this policy</li>
              <li>We will notify you via email or prominent notice on our website</li>
              <li>For material changes affecting your rights, we may request renewed consent</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              We encourage you to review this Privacy Policy periodically for the latest information on our privacy practices.
            </p>
          </section>

          {/* Section 15: Contact Us */}
          <section id="section-15">
            <h2 className="text-2xl font-bold text-white border-l-4 border-primary-500 pl-4">
              15. Contact Us
            </h2>
            <p className="text-gray-300 leading-relaxed mt-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">General Privacy Inquiries</h4>
                <p className="text-gray-400 text-sm">
                  Email: <a href="mailto:privacy@podstack.ai" className="text-primary-400 hover:underline">privacy@podstack.ai</a>
                </p>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">Data Protection Officer (GDPR)</h4>
                <p className="text-gray-400 text-sm">
                  Email: <a href="mailto:dpo@podstack.ai" className="text-primary-400 hover:underline">dpo@podstack.ai</a>
                </p>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">Grievance Officer (India DPDP)</h4>
                <p className="text-gray-400 text-sm">
                  Email: <a href="mailto:grievance@podstack.ai" className="text-primary-400 hover:underline">grievance@podstack.ai</a>
                </p>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-xl border border-gray-800">
                <h4 className="font-semibold text-primary-400 mb-3">Data Subject Requests</h4>
                <p className="text-gray-400 text-sm">
                  Email: <a href="mailto:dsr@podstack.ai" className="text-primary-400 hover:underline">dsr@podstack.ai</a><br />
                  Response Time: Within 30 days
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-primary-500/10 to-transparent rounded-xl border border-primary-500/30">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">EU Representative:</strong> For users in the European Union, you may also contact our EU representative at <a href="mailto:eu-rep@podstack.ai" className="text-primary-400 hover:underline">eu-rep@podstack.ai</a>
              </p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 px-6 relative z-10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <PixelLogo scale={0.6} animate={false} />
            <span className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Podstack. All rights reserved.</span>
          </div>
          <div className="flex flex-wrap gap-6 text-gray-500 text-sm">
            <span className="text-primary-400">Privacy Policy</span>
            <a href="https://podstack.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors">Terms of Service</a>
            <Link to="/contact-us" className="hover:text-primary-400 transition-colors">Contact Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
